import { useState, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Link2, Search, Unlink, AlertTriangle, CheckCircle2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import { formatCurrency } from "../lib/calculations";
import { useToast } from "./ui/ToastModal";
import type { ClaimsVariation } from "../types/database.types";
import {
  linkVariationsToReport,
  unlinkVariationsFromReport,
  variationEffectiveAmount,
} from "../lib/linkVariations";

// ─── Helpers ──────────────────────────────────────────────────
function shortenDocket(docket: string): string {
  if (!docket) return "Untitled";
  const lastNum = docket.match(/-(\d+)$/)?.[1];
  if (lastNum) return `SEQ-${lastNum}`;
  if (docket.length > 30) return docket.slice(0, 27) + "...";
  return docket;
}

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// ─── Types ────────────────────────────────────────────────────
interface LinkVariationsModalProps {
  reportId: string;
  claimNumber: number;
  claimMonth: number;
  claimYear: number;
  projectId: string;
  /** Report's variation totals for comparison */
  reportVariationTotal: number;
  /** Currently linked variation IDs (from claimed_set) */
  currentLinkedIds: string[];
  onClose: () => void;
  onLinked: () => void;
}

// ─── Component ────────────────────────────────────────────────
export function LinkVariationsModal({
  reportId,
  claimNumber,
  claimMonth,
  claimYear,
  projectId,
  reportVariationTotal,
  currentLinkedIds,
  onClose,
  onLinked,
}: LinkVariationsModalProps) {
  const { addToast } = useToast();
  const [allVariations, setAllVariations] = useState<ClaimsVariation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(
    new Set(currentLinkedIds)
  );

  // ─── Fetch all project variations on mount ──────────────────
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("claims_variations")
        .select("*")
        .eq("project_id", projectId)
        .eq("status", "valid")
        // Never offer a variation sync flagged as gone from Dashpivot: it may
        // already be linked to a claim under the project it moved to.
        .is("dashpivot_removed_at", null)
        .order("variation_date", { ascending: false });

      if (error) {
        addToast({ type: "error", title: "Failed to load variations" });
        setLoading(false);
        return;
      }
      setAllVariations(data || []);
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Filter + sort ──────────────────────────────────────────
  const filteredVariations = useMemo(() => {
    const q = search.toLowerCase().trim();
    let filtered = allVariations;
    if (q) {
      filtered = allVariations.filter(
        (v) =>
          v.docket_no?.toLowerCase().includes(q) ||
          v.title?.toLowerCase().includes(q) ||
          shortenDocket(v.docket_no).toLowerCase().includes(q)
      );
    }

    // Sort: awaiting (has waiting_amount > 0) first, then paid
    return [...filtered].sort((a, b) => {
      const aAwaiting = Number(a.waiting_amount ?? 0) > 0 ? 0 : 1;
      const bAwaiting = Number(b.waiting_amount ?? 0) > 0 ? 0 : 1;
      if (aAwaiting !== bAwaiting) return aAwaiting - bAwaiting;
      // Within same group, sort by date descending (newest first)
      return (b.variation_date || "").localeCompare(a.variation_date || "");
    });
  }, [allVariations, search]);

  // ─── Grouped sections ──────────────────────────────────────
  const awaitingVars = useMemo(
    () => filteredVariations.filter((v) => Number(v.waiting_amount ?? 0) > 0),
    [filteredVariations]
  );
  const paidVars = useMemo(
    () => filteredVariations.filter((v) => Number(v.waiting_amount ?? 0) <= 0),
    [filteredVariations]
  );

  // ─── Totals ─────────────────────────────────────────────────
  const selectedTotal = useMemo(() => {
    return allVariations
      .filter((v) => selected.has(v.id))
      .reduce((sum, v) => sum + variationEffectiveAmount(v), 0);
  }, [allVariations, selected]);

  const totalsDiff = Math.abs(selectedTotal - reportVariationTotal);
  const totalsMatch = totalsDiff < 0.01;

  // ─── Toggle selection ────────────────────────────────────────
  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(filteredVariations.map((v) => v.id)));
  }, [filteredVariations]);

  const deselectAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  // ─── Save ───────────────────────────────────────────────────
  const handleLink = useCallback(async () => {
    if (selected.size === 0) {
      addToast({ type: "error", title: "Select at least one variation" });
      return;
    }
    setSaving(true);
    const selectedVars = allVariations.filter((v) => selected.has(v.id));
    const result = await linkVariationsToReport(reportId, selectedVars);
    setSaving(false);

    if (result.success) {
      addToast({ type: "success", title: `Linked ${selectedVars.length} variation(s) to Claim #${claimNumber}` });
      onLinked();
    } else {
      addToast({ type: "error", title: result.error || "Failed to link variations" });
    }
  }, [selected, allVariations, reportId, claimNumber, addToast, onLinked]);

  // ─── Unlink ─────────────────────────────────────────────────
  const handleUnlink = useCallback(async () => {
    setSaving(true);
    const result = await unlinkVariationsFromReport(reportId);
    setSaving(false);

    if (result.success) {
      addToast({ type: "success", title: "Variations unlinked, using date-based lookup" });
      onLinked();
    } else {
      addToast({ type: "error", title: result.error || "Failed to unlink" });
    }
  }, [reportId, addToast, onLinked]);

  // ─── Variation row ──────────────────────────────────────────
  const renderVariationRow = (v: ClaimsVariation) => {
    const isSelected = selected.has(v.id);
    const effective = variationEffectiveAmount(v);
    const isAwaiting = Number(v.waiting_amount ?? 0) > 0;
    const vDate = v.variation_date
      ? new Date(v.variation_date)
      : null;
    const dateStr = vDate
      ? `${MONTH_SHORT[vDate.getMonth()]} ${vDate.getFullYear()}`
      : "—";

    return (
      <label
        key={v.id}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderRadius: 8,
          cursor: "pointer",
          background: isSelected ? "rgba(59,130,246,0.06)" : "transparent",
          border: isSelected
            ? "1px solid rgba(59,130,246,0.25)"
            : "1px solid transparent",
          transition: "all 0.15s",
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggle(v.id)}
          style={{
            width: 18,
            height: 18,
            accentColor: "#3b82f6",
            cursor: "pointer",
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontWeight: 600,
              fontSize: "0.85rem",
            }}
          >
            <span>{shortenDocket(v.docket_no)}</span>
            {!isAwaiting && (
              <span
                style={{
                  fontSize: "0.55rem",
                  fontWeight: 700,
                  background: "#dcfce7",
                  color: "#16a34a",
                  padding: "1px 6px",
                  borderRadius: 3,
                  textTransform: "uppercase",
                }}
              >
                paid
              </span>
            )}
          </div>
          {v.title && (
            <div
              style={{
                fontSize: "0.72rem",
                color: "var(--text-muted)",
                marginTop: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {v.title}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div data-seq-private="" style={{ fontWeight: 600, fontSize: "0.85rem" }}>
            {formatCurrency(effective, true)}
          </div>
          <div
            style={{
              fontSize: "0.65rem",
              color: "var(--text-muted)",
            }}
          >
            {dateStr}
          </div>
        </div>
      </label>
    );
  };

  // ─── Render ─────────────────────────────────────────────────
  const period = `${MONTH_SHORT[claimMonth - 1]}-${String(claimYear).slice(-2)}`;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--bg-primary, #fff)",
          borderRadius: 16,
          width: "min(520px, 90vw)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid var(--border-color-light)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Link2 size={18} style={{ color: "#3b82f6" }} />
            <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>
              Link Variations to Claim #{claimNumber}{" "}
              <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
                ({period})
              </span>
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              borderRadius: 6,
              color: "var(--text-muted)",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Search + bulk actions */}
        <div style={{ padding: "12px 20px 8px", display: "flex", gap: 8 }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "var(--bg-secondary)",
              borderRadius: 8,
              padding: "6px 10px",
              border: "1px solid var(--border-color-light)",
            }}
          >
            <Search size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search variations…"
              style={{
                border: "none",
                background: "transparent",
                outline: "none",
                width: "100%",
                fontSize: "0.82rem",
                color: "var(--text-primary)",
              }}
            />
          </div>
          <button
            onClick={selectAll}
            style={{
              fontSize: "0.7rem",
              fontWeight: 600,
              background: "var(--bg-secondary)",
              border: "1px solid var(--border-color-light)",
              borderRadius: 6,
              padding: "4px 10px",
              cursor: "pointer",
              color: "#3b82f6",
              whiteSpace: "nowrap",
            }}
          >
            All
          </button>
          <button
            onClick={deselectAll}
            style={{
              fontSize: "0.7rem",
              fontWeight: 600,
              background: "var(--bg-secondary)",
              border: "1px solid var(--border-color-light)",
              borderRadius: 6,
              padding: "4px 10px",
              cursor: "pointer",
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
            }}
          >
            None
          </button>
        </div>

        {/* List */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "4px 14px 12px",
          }}
        >
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: 40,
                color: "var(--text-muted)",
                fontSize: "0.82rem",
              }}
            >
              Loading variations…
            </div>
          ) : allVariations.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: 40,
                color: "var(--text-muted)",
                fontSize: "0.82rem",
              }}
            >
              No variations found for this project.
            </div>
          ) : (
            <>
              {/* Awaiting section */}
              {awaitingVars.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      color: "#f59e0b",
                      padding: "10px 14px 4px",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Awaiting Payment ({awaitingVars.length})
                  </div>
                  {awaitingVars.map(renderVariationRow)}
                </>
              )}

              {/* Paid section */}
              {paidVars.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      color: "#16a34a",
                      padding: awaitingVars.length > 0 ? "16px 14px 4px" : "10px 14px 4px",
                      letterSpacing: "0.04em",
                      borderTop: awaitingVars.length > 0 ? "1px solid var(--border-color-light)" : "none",
                      marginTop: awaitingVars.length > 0 ? 8 : 0,
                    }}
                  >
                    Paid ({paidVars.length})
                  </div>
                  {paidVars.map(renderVariationRow)}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer: totals + actions */}
        <div
          style={{
            borderTop: "1px solid var(--border-color-light)",
            padding: "12px 20px",
            background: "var(--bg-secondary)",
          }}
        >
          {/* Totals comparison */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
              fontSize: "0.78rem",
            }}
          >
            <div>
              <span style={{ color: "var(--text-muted)" }}>Selected:</span>{" "}
              <span style={{ fontWeight: 700 }}>
                {selected.size} variation{selected.size !== 1 ? "s" : ""}
              </span>
              <span style={{ color: "var(--text-muted)", margin: "0 6px" }}>│</span>
              <span style={{ color: "var(--text-muted)" }}>Total:</span>{" "}
              <span data-seq-private="" style={{ fontWeight: 700 }}>
                {formatCurrency(selectedTotal)}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {totalsMatch ? (
                <>
                  <CheckCircle2 size={13} style={{ color: "#16a34a" }} />
                  <span style={{ color: "#16a34a", fontWeight: 600, fontSize: "0.72rem" }}>
                    Totals match
                  </span>
                </>
              ) : selected.size > 0 ? (
                <>
                  <AlertTriangle size={13} style={{ color: "#f59e0b" }} />
                  <span data-seq-private="" style={{ color: "#f59e0b", fontWeight: 600, fontSize: "0.72rem" }}>
                    Expects {formatCurrency(reportVariationTotal)}
                  </span>
                </>
              ) : null}
            </div>
          </div>

          {/* Mismatch warning */}
          {selected.size > 0 && !totalsMatch && (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fde68a",
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 10,
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                fontSize: "0.72rem",
                color: "#92400e",
              }}
            >
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span data-seq-private="">
                Selected total ({formatCurrency(selectedTotal)}) doesn't match the
                report variation total ({formatCurrency(reportVariationTotal)}).
                Some variations may be missing or amounts changed since the report
                was generated.
              </span>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <div>
              {currentLinkedIds.length > 0 && (
                <button
                  onClick={handleUnlink}
                  disabled={saving}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid #fca5a5",
                    background: "#fef2f2",
                    color: "#dc2626",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.5 : 1,
                  }}
                >
                  <Unlink size={14} />
                  Unlink
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={onClose}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "1px solid var(--border-color-light)",
                  background: "var(--bg-primary)",
                  color: "var(--text-primary)",
                  fontWeight: 500,
                  fontSize: "0.78rem",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLink}
                disabled={saving || selected.size === 0}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: saving || selected.size === 0 ? "#94a3b8" : "#3b82f6",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  cursor:
                    saving || selected.size === 0 ? "not-allowed" : "pointer",
                  transition: "background 0.15s",
                }}
              >
                <Link2 size={14} />
                {saving
                  ? "Saving…"
                  : `Link ${selected.size} Variation${selected.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
